<template>
  <el-select-v2
    v-model="innerValue"
    :multiple="multiple"
    :collapse-tags="collapseTags"
    :max-collapse-tags="maxCollapseTags"
    clearable
    filterable
    :teleported="true"
    :placeholder="placeholder"
    :suffix-icon="ArrowDownSvg"
    popper-class="custom-popper"
    style="width: 100%"
    :options="filteredOptions"
    :height="287"
    :item-height="52"
    :filter-method="handleFilterMethod"
    :disabled="disableDropdown"
    @visible-change="handleVisibleChange"
  >
    <template #header v-if="showHeader">
      <div class="suggested-header"> 
        {{ header }}
      </div>
    </template>
    <template #default="{ item }">
      <div :data-testid="optionTestId(item)" class="patient-option">
        <div class="patient-name">
          <HighlightText :text="item.displayName" :query="searchQuery" />
        </div>
        <div class="patient-email" v-if="item.email">
          <HighlightText :text="item.email" :query="searchQuery" />
        </div>
      </div>
    </template>
  </el-select-v2>
</template>

<script setup>
import { ref, watch, computed, useAttrs } from 'vue'
import ArrowDownSvg from '@/views/my-calendar/components/ArrowDownSvg.vue'
import HighlightText from './HighlightText.vue'

const searchQuery = ref('')

// Options inherit the parent's data-testid namespace so each row is addressable,
// e.g. my-calendar.schedule-appointment-panel.patient-select.option.<id>
const attrs = useAttrs()
const optionTestId = (item) => {
  const prefix = attrs['data-testid']
  if (!prefix) return undefined
  return `${prefix}.option.${item.value}`
}

// Derive showHeader from searchQuery without side effects
const showHeader = computed(() => {
  return !searchQuery.value || searchQuery.value.trim() === ''
})

const props = defineProps({
  modelValue: {
    type: [Array, String, Number],
    default: () => []
  },  
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  collapseTags: {
    type: Boolean,
    default: false,
  },
  maxCollapseTags: {
    type: Number,
    default: 1,
  },
  header: {
    type: String,
    default: 'Suggested Patients'
  },
  disableDropdown: {
    type: Boolean,
    default: false
  }
})



// Format options for el-select-v2 (requires value/label format)
// el-select-v2 has built-in virtual scrolling, so no manual optimization needed
const formattedOptions = computed(() => {
  if (!props.options || props.options.length === 0) return []
  
  // Convert from {id, name, email} to {value, label} format required by el-select-v2
  return props.options.map(opt => ({
    value: opt.id,
    label: opt.name,
    displayName: opt.name,
    email: opt.email || '',
    // Keep original data for reference
    calendar_id: opt.calendar_id
  }))
})

// Filtered options based on search query
const filteredOptions = computed(() => {
  const query = searchQuery.value
  
  // If no query, show all options
  if (!query || query.trim() === '') {
    return formattedOptions.value
  }
  
  // Filter based on name and email
  const searchLower = query.toLowerCase()
  return formattedOptions.value.filter(option => {
    const nameMatch = option.label?.toLowerCase().includes(searchLower)
    const emailMatch = option.email?.toLowerCase().includes(searchLower)
    return nameMatch || emailMatch
  })
})

// Handle filter method to capture search input
const handleFilterMethod = (query) => {
  searchQuery.value = query
  return true // Always return true, let computed filteredOptions handle the filtering
}

// Handle dropdown visibility change
const handleVisibleChange = (visible) => {

  if (!visible) {
    // Reset search query when dropdown closes (showHeader will auto-update via computed)
    searchQuery.value = ''
  }
}


const emit = defineEmits(['update:modelValue'])

const innerValue = ref(props.modelValue ?? [])

watch(() => props.modelValue, (val) => {
  innerValue.value = val ?? []
})

watch(innerValue, (val) => {
  emit('update:modelValue', val)
})

const checkAll = ref(false)
const indeterminate = ref(false)

watch(innerValue, (val) => {
  if(!val) return;
  if (val.length === 0) {
    checkAll.value = false
    indeterminate.value = false
  } else if (val.length === props.options.length) {
    checkAll.value = true
    indeterminate.value = false
  } else {
    checkAll.value = false
    indeterminate.value = true
  }
})


</script>

<style scoped>
.custom-header .el-checkbox {
  display: flex;
  height: unset;
}

.suggested-header {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #666666;
  letter-spacing: 0.15px;
  padding: 0;
  margin-bottom: 2px;
}

.patient-option {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.patient-name {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: normal;
  color: #333333;
  letter-spacing: 0.15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.patient-email {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: #999999;
  letter-spacing: 0.15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

}

</style>

